import { breakpoints, Button, Dialog, Inline, LinkButton, Stack, Text, vars } from '@etg/wings';
import { useDirection, useTranslation } from '@eti/providers';
import { LoadingDots, Pictogram } from '@eti/components';
import { useHistory, useLocation } from 'react-router-dom';
import { AuthenticationType, LoginSource } from '@eti/schema-types';
import { css } from '@eti/styles';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import { useMutation } from '@apollo/client';
import { SEND_CHALLENGE_EMAIL_WITH_CODE } from './graphql/mutations';
import { EmailChallengedState } from './models';
import { useAuth } from './AuthContext';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const linkTextStyles = css`
  @media (max-width: ${breakpoints._768}) {
    padding-bottom: 32px;
  }
`;
const iconStyles = css`
  height: 64px;
  width: 64px;

  @media (min-width: ${breakpoints._768}) {
    height: 126px;
    width: 126px;
  }
`;
const EmailChallengedDialog = _ref => {
  var _sendChallengeEmailRe;
  let {
    brandCode,
    isOpen,
    handleCloseButtonClick,
    emailChallengedInfo,
    unauthorizedRedirectionUrl
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    isRTL
  } = useDirection();
  const history = useHistory();
  const location = useLocation();
  const {
    routes
  } = window.spa_variables;
  const {
    authenticateUser,
    authenticateUserResult
  } = useAuth();
  const [sendChallengeEmail, sendChallengeEmailResult] = useMutation(SEND_CHALLENGE_EMAIL_WITH_CODE);
  const isSendChallengeEmailSuccess = Boolean((_sendChallengeEmailRe = sendChallengeEmailResult.data) === null || _sendChallengeEmailRe === void 0 || (_sendChallengeEmailRe = _sendChallengeEmailRe.sendChallengeEmail) === null || _sendChallengeEmailRe === void 0 ? void 0 : _sendChallengeEmailRe.success);

  // TODO: A property should be created
  const expirationTime = '10';
  const CRYPTIC_CODE_QUERY_KEY = 'c';
  const crypticCode = new URLSearchParams(location.search).get(CRYPTIC_CODE_QUERY_KEY);
  const dialogContentMapping = {
    [EmailChallengedState.REQUIRED]: {
      title: t('IbeClient.EmailChallenged.Modal.Title'),
      description: t('IbeClient.EmailChallenged.Modal.Description', emailChallengedInfo === null || emailChallengedInfo === void 0 ? void 0 : emailChallengedInfo.email),
      buttonText: t('IbeClient.EmailChallenged.Modal.Button'),
      icon: /*#__PURE__*/_jsx(Pictogram, {
        brand: brandCode,
        className: iconStyles,
        "data-testid": "email-verification-pictogram",
        id: "email-verification",
        mirrored: isRTL
      })
    },
    [EmailChallengedState.CHALLENGED]: {
      title: t('IbeClient.EmailChallenged.Success.Modal.Title'),
      description: t('IbeClient.EmailChallenged.Success.Modal.Description', expirationTime),
      linkText: t('IbeClient.EmailChallenged.Modal.ResendEmail.Text'),
      buttonText: t('Close'),
      icon: /*#__PURE__*/_jsx(CheckCircle, {
        "aria-label": t('ScreenReader.SuccessIcon.AriaLabel.Text'),
        className: iconStyles,
        color: vars.colors.signal.positive.foreground,
        weight: "fill"
      })
    },
    [EmailChallengedState.EXPIRED]: {
      title: t('IbeClient.EmailChallenged.Expired.Modal.Title'),
      description: t('IbeClient.EmailChallenged.Expired.Modal.Description', expirationTime),
      linkText: t('IbeClient.EmailChallenged.Modal.ResendEmail.Text'),
      buttonText: t('Close'),
      icon: /*#__PURE__*/_jsx(XCircle, {
        "aria-label": t('ScreenReader.FailureIcon.AriaLabel.Text'),
        className: iconStyles,
        color: vars.colors.signal.critical.foreground,
        weight: "fill"
      })
    },
    [EmailChallengedState.FAILED]: {
      title: t('IbeClient.EmailChallenged.Failure.Modal.Title'),
      description: t('IbeClient.EmailChallenged.Failure.Modal.Description'),
      linkText: t('IbeClient.EmailChallenged.Modal.ResendEmail.Text'),
      buttonText: t('Close'),
      icon: /*#__PURE__*/_jsx(XCircle, {
        "aria-label": t('ScreenReader.FailureIcon.AriaLabel.Text'),
        className: iconStyles,
        color: vars.colors.signal.critical.foreground,
        weight: "fill"
      })
    }
  };
  const isOnAnyLoginPage = [routes['rf.order.authentication.action'], routes['postbooking.authentication.action'], routes['rf.contact.action'], routes['rf.ryanair-login.action']].some(route => location.pathname.endsWith(route));
  const getLoginSource = () => {
    switch (true) {
      case location.pathname.endsWith(routes['postbooking.authentication.action']):
        return LoginSource.PostbookingLogin;
      case location.pathname.endsWith(routes['rf.contact.action']):
        return LoginSource.ContactUsLogin;
      case location.pathname.endsWith(routes['rf.ryanair-login.action']):
        return LoginSource.RyanairLogin;
      default:
        return LoginSource.OrderLogin;
    }
  };
  const redirectToAuthenticateEmailChallenged = () => {
    if (isOnAnyLoginPage) {
      history.go(0);
    }
    history.push(unauthorizedRedirectionUrl);
    handleCloseButtonClick();
  };
  const sendVerificationEmail = async () => {
    if (!(emailChallengedInfo !== null && emailChallengedInfo !== void 0 && emailChallengedInfo.email) && !(emailChallengedInfo !== null && emailChallengedInfo !== void 0 && emailChallengedInfo.orderNumber) && crypticCode) {
      await sendChallengeEmail({
        variables: {
          crypticCode
        }
      });
    } else if (emailChallengedInfo) {
      await authenticateUser({
        allowSendingChallengeEmail: true,
        email: emailChallengedInfo.email,
        orderNumber: emailChallengedInfo.orderNumber,
        loginSource: getLoginSource(),
        authenticationType: AuthenticationType.OrderInfo
      });
    }
  };
  if (!emailChallengedInfo || !dialogContentMapping[emailChallengedInfo.instruction]) {
    return null;
  }
  const handleClick = () => {
    if (isSendChallengeEmailSuccess) {
      history.go(0);
    } else if (emailChallengedInfo.instruction === EmailChallengedState.REQUIRED) {
      sendVerificationEmail();
    } else {
      redirectToAuthenticateEmailChallenged();
    }
  };
  const dialogContent = dialogContentMapping[isSendChallengeEmailSuccess ? EmailChallengedState.CHALLENGED : emailChallengedInfo.instruction];
  return /*#__PURE__*/_jsx(Dialog, {
    closeButtonAriaLabel: t('ScreenReader.CloseButtonDialog.AriaLabel.Text'),
    "data-testid": "email-challenged-dialog",
    illustration: dialogContent.icon,
    isOpen: isOpen,
    title: dialogContent.title,
    children: /*#__PURE__*/_jsxs(Stack, {
      children: [/*#__PURE__*/_jsx(Text, {
        asHtml: true,
        children: dialogContent.description
      }), dialogContent.linkText && /*#__PURE__*/_jsx(LinkButton, {
        className: linkTextStyles,
        onClick: sendVerificationEmail,
        children: dialogContent.linkText
      }), /*#__PURE__*/_jsx(Inline, {
        align: "end",
        collapseBelow: breakpoints._560,
        spacing: 8,
        children: /*#__PURE__*/_jsx(Button, {
          "data-testid": "log-in-button",
          onClick: handleClick,
          variant: "primary",
          children: authenticateUserResult.loading || sendChallengeEmailResult.loading ? /*#__PURE__*/_jsx(LoadingDots, {}) : dialogContent.buttonText
        })
      })]
    })
  }, emailChallengedInfo.instruction);
};
export default EmailChallengedDialog;
//# sourceMappingURL=EmailChallengedDialog.js.map